import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Returns the URL normalised when it parses as an absolute http: or https:
 * URL, otherwise null. Playlist URL columns (youtube_playlist_url and
 * friends) are row data a user can write, so a javascript:, data: or
 * vbscript: value must never reach window.open, location.href or an href.
 * The URL parser strips leading whitespace and lowercases the scheme, so
 * " JavaScript:..." is caught by the same protocol check. A relative or
 * protocol-relative value is refused because it has no base here.
 */
export function safeExternalUrl(url: unknown): string | null {
  if (typeof url !== 'string' || url.trim() === '') return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
  return parsed.href;
}

/**
 * Opens a URL in a new window/tab with popup blocker detection.
 * Falls back to navigating the current window if popup is blocked.
 * Refuses (no-op plus a console warning) anything safeExternalUrl rejects.
 */
export function safeWindowOpen(url: string, target = '_blank', features?: string): void {
  const safe = safeExternalUrl(url);
  if (!safe) {
    console.warn('safeWindowOpen refused a URL that is not absolute http(s)');
    return;
  }
  const newWindow = window.open(safe, target, features);
  // window.open returns null by design when the features carry noopener, so
  // null means "blocked" only without it. Falling back then would open the
  // link twice: in the new tab and in this one.
  if (features && /\bnoopener\b/i.test(features)) return;
  if (!newWindow || newWindow.closed) {
    // Popup was blocked: navigate in the current tab as a fallback
    window.location.href = safe;
  }
}

/**
 * A mail-compose link with an encoded subject and body. A fixed mailto:
 * scheme, so it does not go through safeWindowOpen, which only opens http(s).
 */
export function mailtoUrl(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * Safe localStorage.setItem that catches QuotaExceededError and SecurityError.
 */
export function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // QuotaExceededError or SecurityError — silently fail
  }
}

/**
 * Safe localStorage.getItem that catches SecurityError (Safari private browsing, cross-origin iframes).
 */
export function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Safe localStorage.removeItem that catches SecurityError.
 */
export function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // SecurityError — localStorage unavailable, silently fail
  }
}

/** Strip markdown formatting from text for plain-text display (e.g. blog excerpts). */
export function stripMarkdown(text: string): string {
  return text
    .replace(/<[^>]+>/g, '')           // HTML tags
    // An HTML tag cut off before its '>' (a truncated excerpt). Requires a tag
    // name and an attribute, so text like "I <3 this" survives.
    .replace(/<\/?[a-zA-Z][\w-]*\s+[\w-]+\s*=[^>]*$/, '')
    .replace(/^#{1,6}\s+/gm, '')       // headings
    .replace(/\*\*(.+?)\*\*/g, '$1')   // bold
    .replace(/\*(.+?)\*/g, '$1')       // italic
    .replace(/__(.+?)__/g, '$1')       // bold (underscore)
    .replace(/_(.+?)_/g, '$1')         // italic (underscore)
    .replace(/~~(.+?)~~/g, '$1')       // strikethrough
    .replace(/`(.+?)`/g, '$1')         // inline code
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // images, before links so "![a](u)" does not leave "!a"
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/^\s*[-*+]\s+/gm, '')     // unordered lists
    .replace(/^\s*\d+\.\s+/gm, '')     // ordered lists
    .replace(/^>\s+/gm, '')            // blockquotes
    .replace(/\s{2,}/g, ' ')           // collapse whitespace
    .trim();
}

/** Format a date string consistently using Intl.DateTimeFormat */
export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  }).format(d);
}


const eurFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' });

/** Format a number as EUR currency (e.g. €1.99) */
export function formatCurrency(amount: number): string {
  return eurFormatter.format(amount);
}

/** Format raw credit ledger reason strings for user-friendly display */
export function formatCreditReason(reason: string): string {
  const reasonMap: Record<string, string> = {
    'pro_playlist_generation': 'Expert Curation generation',
    'apple_music_survey_completion': 'Apple Music survey',
    'test': 'Test transaction',
    'achievement_coin': 'Achievement reward',
    'generation_failed_refund': 'Refund for a failed generation',
    'retroactive_refund_failed_generations': 'Refund for a failed generation',
  };

  if (reasonMap[reason]) return reasonMap[reason];
  if (reason.startsWith('apple_music_survey_completion')) return 'Apple Music survey';
  if (reason.includes('Referral bonus')) return reason;
  if (reason.includes('Coin purchase')) return 'Coin purchase';
  if (reason.includes('Gift bonus')) return 'Gift bonus';
  if (reason.includes('Coupon redemption')) return reason.replace('Coupon redemption:', 'Coupon:');
  if (/^(goodwill|thank you)/i.test(reason)) return 'Goodwill credit';

  // Anything else is an internal code or an admin note, and admin notes have
  // named QA scripts and file paths. Never show the raw text.
  return 'Balance adjustment';
}
